import type { EmitFn } from "../adapters.js";
import { detectArticle } from "./reading.js";

/**
 * Tiempo VIEWABLE por FRANJA (mapa de atención por URL) — el núcleo del producto.
 *
 * Mide cuántos ms reales estuvo cada FRANJA del documento dentro del viewport, con
 * la pestaña visible y el usuario presente. La identidad de franja es POSICIONAL
 * (deciles del % del alto del documento: "0-10" … "90-100"), no semántica: los
 * publishers reeditan mucho sus blogs, así que una identidad por H2/sección se
 * rompería; el % es estable ante ediciones e independiente del dispositivo. Es la
 * misma definición del paper de Lagun & Lalmas (WSDM'16): ViewportTime(y) = tiempo
 * que la posición vertical y estuvo en el viewport, reconstruido de scroll + tamaño
 * de ventana (NO IntersectionObserver por elemento).
 *
 * UN SOLO RELOJ: no tiene timer ni listener propio. Se suscribe al tick de tiempo
 * ACTIVO de idle.ts (`onTick`), que entrega los ms ya capados al umbral de inactividad
 * y gateados por visible+presente. Reparte ESE crédito entre las franjas visibles,
 * proporcional a cuánto del viewport ocupa cada una (overlap/innerHeight). Por
 * construcción Σ(tiempo por franja) ≤ activeReadingMs (invariante de conservación, el
 * canario de fiabilidad) y NO hay doble conteo ni segundo reloj que sincronizar.
 *
 * Cada TRAMO cerrado (la franja deja de estar visible, o flush de salida / reset SPA)
 * se emite como un evento DISCRETO `section_time { sec, ms, lbl? }` con su eventId
 * (auditable, deduplicado, idempotente ante reintentos de la cola) — la lección de
 * `ad_impression`, no un mapa-delta con baseline reescribible.
 *
 * Robusto ante DOMs diversos (WordPress, Elementor, Next, MPA/SPA): sin <article> ni
 * headings (etiqueta vacía, la franja % sigue valiendo), documento más corto que el
 * viewport (acredita de menos, nunca de más, nunca rompe), altura dinámica (recalcula
 * la geometría en cada tick). Cualquier excepción se traga: jamás tumba la página.
 */

/** Número de franjas (deciles del documento). */
export const SECTION_BANDS = 10;

/** Piso de ruido: un tramo de menos de 1s NO se emite (scroll de paso, no lectura).
 *  Lagun & Lalmas §4.3: "we ignore viewport positions that lasted less than one second". */
export const MIN_TRAMO_MS = 1000;

type Geom = { docHeight: number; viewportH: number; scrollY: number };
type Heading = { top: number; text: string };

/** Entorno inyectable: en producción lee window/document; en tests, falsos. */
export type SectionEnv = {
  readGeom: () => Geom;
  /** Encabezados con su posición vertical (px en el documento) para etiquetar franjas. */
  readHeadings: () => Heading[];
  /** location.pathname (la URL a la que atribuir los tramos de ESTA página). */
  getPath: () => string;
};

/** Registro del tick de tiempo activo (idle.onTick en producción). */
export type OnTick = (cb: (creditedMs: number) => void) => void;

/** Entorno de producción: lee el DOM real. Solo se invoca en navegador (post domReady). */
function domEnv(): SectionEnv {
  return {
    readGeom: () => ({
      docHeight: Math.max(
        document.documentElement?.scrollHeight ?? 0,
        document.body?.scrollHeight ?? 0
      ),
      viewportH: window.innerHeight,
      scrollY: window.scrollY,
    }),
    readHeadings: () => {
      const out: Heading[] = [];
      try {
        const art = detectArticle();
        art.querySelectorAll<HTMLElement>("h2, h3").forEach((h) => {
          // getBoundingClientRect SOLO aquí (al iniciar / reset SPA), nunca por tick ni
          // en scroll → no viola "cero lectura de layout en caliente". Etiqueta cosmética.
          const top = h.getBoundingClientRect().top + window.scrollY;
          const text = (h.innerText || "").trim().slice(0, 60);
          if (text) out.push({ top, text });
        });
      } catch {
        /* DOM inesperado: sin etiquetas, las franjas % siguen valiendo */
      }
      return out;
    },
    getPath: () => (typeof location !== "undefined" ? location.pathname : "/"),
  };
}

/** Id estable de la franja k: rango de % del documento, p. ej. "30-40". */
function bandId(k: number): string {
  const lo = Math.round((k * 100) / SECTION_BANDS);
  const hi = Math.round(((k + 1) * 100) / SECTION_BANDS);
  return `${lo}-${hi}`;
}

export function startSectionTime(
  emit: EmitFn,
  onTick: OnTick,
  env: SectionEnv = domEnv()
): { flush: () => void; reset: () => void } {
  // ms acumulados en el tramo ABIERTO de cada franja (se vacía al cerrar el tramo).
  const openMs: number[] = new Array(SECTION_BANDS).fill(0);
  let labels: string[] = new Array(SECTION_BANDS).fill("");
  // URL a la que atribuir los tramos de la página actual. En SPA el router avisa
  // DESPUÉS de cambiar location, así que guardamos la nuestra y la pasamos como override.
  let pageUrl = env.getPath();

  computeLabels();

  /** Mapea cada encabezado a su franja por posición; primera etiqueta gana. */
  function computeLabels() {
    labels = new Array(SECTION_BANDS).fill("");
    const { docHeight } = env.readGeom();
    if (!(docHeight > 0)) return;
    const bandH = docHeight / SECTION_BANDS;
    for (const h of env.readHeadings()) {
      if (!(h.top >= 0)) continue;
      let k = Math.floor(h.top / bandH);
      if (k < 0) k = 0;
      if (k >= SECTION_BANDS) k = SECTION_BANDS - 1;
      if (!labels[k]) labels[k] = h.text;
    }
  }

  /** Cierra el tramo abierto de la franja k y lo emite (si supera el piso de ruido). */
  function closeBand(k: number) {
    const ms = Math.round(openMs[k]);
    openMs[k] = 0;
    if (ms < MIN_TRAMO_MS) return; // tramo de paso: no es lectura
    const props: Record<string, unknown> = { sec: bandId(k), ms };
    if (labels[k]) props.lbl = labels[k];
    // url override = la página donde se gastó el tiempo (clave en SPA).
    emit("section_time", props, pageUrl);
  }

  /**
   * Reparte el crédito de tiempo activo (ya gateado por idle: visible + presente) entre
   * las franjas visibles, proporcional a la fracción del viewport que ocupa cada una.
   * Σ(fracciones) ≤ 1 ⇒ Σ(tiempo por franja) ≤ activeReadingMs (conservación).
   */
  function accrue(creditedMs: number) {
    if (!(creditedMs > 0)) return; // pestaña oculta / idle: idle.ts no nos llama; defensivo
    const { docHeight: H, viewportH: V, scrollY: top } = env.readGeom();
    if (!(H > 0) || !(V > 0)) return; // doc sin medir aún: no acreditamos a ciegas
    const bandH = H / SECTION_BANDS;
    const vpTop = top;
    const vpBot = top + V;
    for (let k = 0; k < SECTION_BANDS; k++) {
      const bTop = k * bandH;
      const bBot = (k + 1) * bandH;
      const overlap = Math.min(vpBot, bBot) - Math.max(vpTop, bTop);
      if (overlap > 0) {
        openMs[k] += creditedMs * (overlap / V);
      } else if (openMs[k] > 0) {
        // La franja salió del viewport: cierra su tramo (cada visita = un evento).
        closeBand(k);
      }
    }
  }

  onTick(accrue);

  return {
    /** Cierra y emite TODOS los tramos abiertos (salida de página: pagehide / hidden). */
    flush() {
      for (let k = 0; k < SECTION_BANDS; k++) if (openMs[k] > 0) closeBand(k);
    },
    /**
     * Navegación SPA: vuelca los tramos de la página SALIENTE con su URL (la que aún
     * tenemos guardada), luego cambia a la URL nueva y recalcula etiquetas. El llamante
     * (index.ts) solo invoca esto cuando el pathname cambió de verdad.
     */
    reset() {
      for (let k = 0; k < SECTION_BANDS; k++) if (openMs[k] > 0) closeBand(k);
      pageUrl = env.getPath(); // location ya apunta a la página nueva
      computeLabels();
    },
  };
}
